import { absentToday, formatAbsenceRange } from '../../shared/absence';
import type { Announcement } from '../lib/useAnnouncements';
import type { AbsenceState } from '../lib/useAbsences';

const NEWS_COUNT = 3;
const dayMonth = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit' });

/**
 * Die beiden Panels über dem Schwarzen-Brett-Strip (Design-System §6: Glas-Card,
 * `// `-Eyebrow in Press Start 2P, Daten in Space Mono).
 */
export function HomePanels({
  announcements,
  absences,
  onOpenBoard,
}: {
  announcements: Announcement[] | null;
  /** Kommt aus der People-App (/api/absences-today). */
  absences: AbsenceState;
  onOpenBoard: () => void;
}) {
  return (
    <div className="hpanels">
      <NewsPanel items={announcements} onOpen={onOpenBoard} />
      <AbsencePanel state={absences} />
    </div>
  );
}

function NewsPanel({
  items,
  onOpen,
}: {
  items: Announcement[] | null;
  onOpen: () => void;
}) {
  const newest = items?.slice(0, NEWS_COUNT) ?? [];

  return (
    <button type="button" className="hpanel hpanel--news" onClick={onOpen} aria-haspopup="dialog">
      <span className="hpanel__head">
        <span className="hpanel__k">// news</span>
        <span className="hpanel__go" aria-hidden="true">
          &gt;
        </span>
      </span>

      {items === null ? (
        <span className="hpanel__empty">lädt …</span>
      ) : newest.length === 0 ? (
        <span className="hpanel__empty">Nichts angeschlagen.</span>
      ) : (
        <ul className="hpanel__list">
          {newest.map((item) => (
            <li className="hpanel__row" key={item.id}>
              <span className="hpanel__date">{dayMonth.format(new Date(item.created_at))}</span>
              <span className="hpanel__text">{item.title}</span>
              {item.pinned && (
                <span className="hpanel__pin" aria-label="angepinnt" title="angepinnt">
                  ★
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </button>
  );
}

function AbsencePanel({ state }: { state: AbsenceState }) {
  // Kein Grund, keine Art – siehe shared/absence.ts.
  return (
    <section className="hpanel" aria-label="Heute abwesend">
      <span className="hpanel__head">
        <span className="hpanel__k hpanel__k--pink">// heute abwesend</span>
      </span>

      {state.status === 'unconfigured' && (
        <span className="hpanel__empty">
          Anbindung folgt
          <span className="hpanel__note">SDC People liefert die Abwesenheiten.</span>
        </span>
      )}

      {state.status === 'loading' && <span className="hpanel__empty">lädt …</span>}

      {state.status === 'error' && (
        <span className="hpanel__empty">
          Nicht verfügbar
          <span className="hpanel__note">{state.message}</span>
        </span>
      )}

      {state.status === 'ready' &&
        (() => {
          const today = absentToday(state.absences);
          if (today.length === 0) return <span className="hpanel__empty">Heute sind alle da.</span>;
          return (
            <ul className="hpanel__list">
              {today.map((absence) => (
                <li className="hpanel__row" key={absence.id}>
                  <span className="hpanel__text">{absence.name}</span>
                  <span className="hpanel__date">
                    {formatAbsenceRange(absence.from, absence.to)}
                  </span>
                </li>
              ))}
            </ul>
          );
        })()}
    </section>
  );
}
